'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useHomeTradeStats } from '@/features/home/hooks/use-home-trade-stats'
import { type AssetExposure, baseAssetOf } from '@/features/home/lib/home-exposure'
import { buildGauges, type Gauge } from '@/features/home/lib/home-gauges'
import type { TradeIdea } from '@/features/home/lib/home-ideas'
import {
  buildOpportunities,
  type MarketSignal,
  type Relevance,
  type ScreenerFlagName,
} from '@/features/home/lib/home-opportunities'
import type { PositionRisk } from '@/features/home/lib/home-risk'
import {
  applyScreenerFrame,
  emptyScreenerState,
  SCREENER_EXCHANGES,
  screenerFilterToken,
  type ScreenerState,
} from '@/features/screener/lib/screener-frame'
import { useCapabilities } from '@/hooks/use-capabilities'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { useOrderEligibleAccounts } from '@/service/account.hook'
import { useEtfFundFlows } from '@/service/etf-flow.hook'
import { useTokenUnlocksInfinite } from '@/service/tokenomist.hook'
import { useTreeNewsHistory } from '@/service/tree-news.hook'
import { useGetWatchlists } from '@/service/watchlist.hook'
import { useMarketScreenerStream } from '@/service/ws/use-market-screener-stream'
import { StreamKind } from '@/types/generated/common/account'
import type { Exchange } from '@/types/generated/common/types'
import {
  type MarketScreenerFrame,
  ScreenerFlag,
  type ScreenerRow,
} from '@/types/generated/public_api'

const DAY_MS = 86_400_000
const RESYNC_RETRY_MS = 1_000
/** Rows change every second; the ideas only need a fresh read now and then. */
const REFRESH_MS = 60_000
const ETF_ASSETS = ['BTC', 'ETH', 'SOL'] as const

const FLAG_NAMES: Partial<Record<ScreenerFlag, ScreenerFlagName>> = {
  [ScreenerFlag.SCREENER_FLAG_PUMPING]: 'pumping',
  [ScreenerFlag.SCREENER_FLAG_DUMPING]: 'dumping',
  [ScreenerFlag.SCREENER_FLAG_HIGH_VOL]: 'highVol',
  [ScreenerFlag.SCREENER_FLAG_SQUEEZE]: 'squeeze',
  [ScreenerFlag.SCREENER_FLAG_OI_SURGE]: 'oiSurge',
  [ScreenerFlag.SCREENER_FLAG_THIN_BOOK]: 'thinBook',
  [ScreenerFlag.SCREENER_FLAG_FUNDING_EXTREME]: 'fundingExtreme',
}

/** A screener row as the playbook reads it (funding arrives as a fraction; the rest in %). */
export function signalOf(row: ScreenerRow): MarketSignal | null {
  const price = row.live?.price ?? 0
  if (!(price > 0)) return null
  const w15 = row.windows?.w15m
  const apr = (value: number | undefined) => (value == null ? null : value * 100)
  return {
    asset: row.asset.toUpperCase(),
    price,
    change24h: row.live?.changePct24h ?? null,
    change15m: w15?.changePct ?? null,
    stdev15m: w15?.stdevPct ?? null,
    takerBuyPct15m: w15?.takerBuyPct ?? null,
    liqLongUsd15m: w15?.liqLongUsd ?? null,
    liqShortUsd15m: w15?.liqShortUsd ?? null,
    fundingApr: apr(row.derivs?.fundingApr),
    oiChange1h: row.derivs?.oiChangePct1h ?? null,
    oiChange24h: row.derivs?.oiChangePct24h ?? null,
    oiUsd: row.derivs?.openInterestUsd ?? null,
    flags: new Set(
      (row.flags?.values ?? [])
        .map((flag) => FLAG_NAMES[flag])
        .filter((name): name is ScreenerFlagName => Boolean(name))
    ),
    venues: row.venues.map((venue) => ({
      exchange: venue.exchange,
      symbol: venue.symbol,
      fundingApr: apr(venue.derivs?.fundingApr),
    })),
  }
}

/** Every venue's screener rows, kept current from the stream (snapshot, then deltas). */
function useScreenerSnapshot(enabled: boolean): ScreenerRow[] {
  const token = screenerFilterToken(SCREENER_EXCHANGES)
  const stateRef = useRef<ScreenerState>(emptyScreenerState(token))
  const [rows, setRows] = useState<ScreenerRow[]>([])
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushRef = useRef<() => number>(() => 0)
  const payload = useMemo(() => ({ exchanges: [] as Exchange[] }), [])

  const resync = useCallback(() => {
    if (retryRef.current) clearTimeout(retryRef.current)
    retryRef.current = null
    if (flushRef.current() === 0) retryRef.current = setTimeout(resync, RESYNC_RETRY_MS)
  }, [])
  useEffect(
    () => () => {
      if (retryRef.current) clearTimeout(retryRef.current)
    },
    []
  )

  const onMessage = useCallback(
    (frame?: MarketScreenerFrame) => {
      if (!frame) return
      const result = applyScreenerFrame(stateRef.current, frame)
      if (result.kind === 'applied') stateRef.current = result.state
      else if (result.kind === 'gap') {
        stateRef.current = { ...stateRef.current, seq: null }
        resync()
      }
    },
    [resync]
  )
  const { flushAndReconnect } = useMarketScreenerStream({ connected: enabled, payload, onMessage })
  flushRef.current = flushAndReconnect

  // Publish the latest rows once a minute (and as soon as the first snapshot lands).
  useEffect(() => {
    if (!enabled) return
    const publish = () => setRows([...stateRef.current.rows.values()])
    const first = setInterval(() => {
      if (stateRef.current.rows.size > 0) {
        publish()
        clearInterval(first)
      }
    }, 500)
    const every = setInterval(publish, REFRESH_MS)
    return () => {
      clearInterval(first)
      clearInterval(every)
    }
  }, [enabled])

  return rows
}

/**
 * Home's live market read: the screener's view of every coin across venues, ETF flows, token
 * unlocks and the day's headlines, weighted by what the user trades (30-day fills), watches
 * (watchlists) and holds. It feeds two things:
 * - opportunities (the playbook in home-opportunities.ts), tradable on the venues the user
 *   has accounts on;
 * - the brief's topic gauges (home-gauges.ts).
 */
export function useHomeOpportunities({
  exposures,
  risks,
  equityUsd,
  enabled,
}: {
  exposures: readonly AssetExposure[]
  risks: readonly PositionRisk[]
  equityUsd: number | null
  enabled: boolean
}): { ideas: TradeIdea[]; gauges: Gauge[]; allowed: boolean; isPending: boolean } {
  const { isStreamKindAllowed } = useCapabilities()
  const allowed = isStreamKindAllowed(StreamKind.STREAM_KIND_MARKET_SCREENER)
  const rows = useScreenerSnapshot(enabled && allowed)
  const { stats } = useHomeTradeStats(30)
  const { data: watchlists } = useGetWatchlists()
  const { accounts } = useOrderEligibleAccounts()
  const [now] = useState(() => Date.now())

  const relevance = useMemo(() => {
    const map = new Map<string, Relevance>()
    const add = (asset: string, next: Relevance) => {
      const current = map.get(asset)
      if (!current || current.weight < next.weight) map.set(asset, next)
    }
    stats?.topInstruments.forEach((entry, index) =>
      add(baseAssetOf(entry.symbol), {
        reason: 'traded',
        weight: 1 - index * 0.1,
        exchange: entry.exchange,
      })
    )
    for (const watchlist of watchlists ?? []) {
      for (const entry of watchlist.orderedEntries) {
        add(baseAssetOf(entry.symbol), {
          reason: 'watched',
          weight: 0.75,
          exchange: entry.exchange,
        })
      }
    }
    for (const exposure of exposures) {
      if (exposure.positions.length > 0) {
        add(exposure.asset, {
          reason: 'held',
          weight: 0.6,
          exchange: exposure.positions[0].position.exchange,
        })
      }
    }
    return map
  }, [stats, watchlists, exposures])

  const accountsByExchange = useMemo(() => {
    const map = new Map<Exchange, string>()
    for (const exchange of SCREENER_EXCHANGES) {
      const venue = exchangeToTradeVenue(exchange)
      const account = accounts.find((candidate) => candidate.tradeVenue === venue)
      if (account) map.set(exchange, account.accountId)
    }
    return map
  }, [accounts])

  const heldSides = useMemo(() => {
    const map = new Map<string, 'long' | 'short'>()
    for (const risk of risks) map.set(baseAssetOf(risk.position.symbol), risk.side)
    return map
  }, [risks])

  // Fundamental and news inputs, over the windows the playbook looks at.
  const [ranges] = useState(() => ({
    etf: { from: String(now - 16 * DAY_MS), to: String(now) },
    unlocks: { from: String(now), to: String(now + 7 * DAY_MS) },
    news: { from: String(now - DAY_MS), to: String(now) },
  }))
  const etfWanted = (asset: string) => enabled && relevance.has(asset)
  const { data: btcFlows } = useEtfFundFlows(
    { asset: 'BTC', ...ranges.etf, byFund: false },
    etfWanted('BTC')
  )
  const { data: ethFlows } = useEtfFundFlows(
    { asset: 'ETH', ...ranges.etf, byFund: false },
    etfWanted('ETH')
  )
  const { data: solFlows } = useEtfFundFlows(
    { asset: 'SOL', ...ranges.etf, byFund: false },
    etfWanted('SOL')
  )
  const { data: unlockPages } = useTokenUnlocksInfinite({ ...ranges.unlocks, limit: 100 }, enabled)
  const { data: news } = useTreeNewsHistory({ ...ranges.news, limit: 100 }, enabled)

  const signals = useMemo(
    () => rows.map(signalOf).filter((signal): signal is MarketSignal => signal !== null),
    [rows]
  )
  const flows = useMemo(() => {
    const map = new Map<string, number[]>()
    ETF_ASSETS.forEach((asset, index) => {
      const data = [btcFlows, ethFlows, solFlows][index]
      if (!data) return
      map.set(
        asset,
        [...data.points]
          .sort((a, b) => Number(a.date) - Number(b.date))
          .map((point) => point.fundFlow)
      )
    })
    return map
  }, [btcFlows, ethFlows, solFlows])
  const headlines = useMemo(
    () =>
      (news?.posts ?? []).flatMap((post) =>
        post.coins.map((coin) => ({
          asset: coin.toUpperCase(),
          title: post.title || post.body,
          at: Number(post.time),
        }))
      ),
    [news]
  )

  const ideas = useMemo(() => {
    if (!enabled || signals.length === 0) return []
    return buildOpportunities({
      signals,
      relevance,
      accountsByExchange,
      heldSides,
      etfFlows: flows,
      unlocks: (unlockPages?.pages ?? [])
        .flatMap((page) => page.events)
        .map((event) => ({
          asset: event.tokenSymbol.toUpperCase(),
          at: Number(event.unlockDate),
          valueUsd: event.totalCliffValue,
          pctOfMcap: event.valueToMarketCap ?? null,
        })),
      news: headlines,
      equityUsd,
      now: Date.now(),
    })
  }, [
    enabled,
    signals,
    relevance,
    accountsByExchange,
    heldSides,
    flows,
    unlockPages,
    headlines,
    equityUsd,
  ])

  // The gauges read the coins the user holds and trades most (BTC and ETH otherwise).
  const gauges = useMemo(() => {
    const focus = [...relevance.entries()]
      .filter(([, value]) => value.reason === 'held' || value.reason === 'traded')
      .sort((a, b) => b[1].weight - a[1].weight)
      .slice(0, 4)
      .map(([asset]) => asset)
    const focusSet = new Set(focus.length ? focus : ['BTC', 'ETH'])
    return buildGauges({
      signals,
      focus,
      etfFlows: flows,
      headlines24h: news
        ? new Set(headlines.filter((h) => focusSet.has(h.asset)).map((h) => `${h.at}:${h.title}`))
            .size
        : undefined,
    })
  }, [signals, relevance, flows, headlines, news])

  return { ideas, gauges, allowed, isPending: enabled && allowed && rows.length === 0 }
}
